import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowDown, ArrowUp, Loader2, X } from "lucide-react";

const STORAGE_KEY = "pacehrm.salaryRegister.exportPrefs";

export function loadSalaryRegisterExportPrefs(companyId) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const all = JSON.parse(raw);
    return all?.[String(companyId || "default")] || all?.default || null;
  } catch {
    return null;
  }
}

export function saveSalaryRegisterExportPrefs(companyId, prefs) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const all = raw ? JSON.parse(raw) : {};
    all[String(companyId || "default")] = {
      ...prefs,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
}

function moveItem(list, index, dir) {
  const next = [...list];
  const j = index + dir;
  if (j < 0 || j >= next.length) return next;
  [next[index], next[j]] = [next[j], next[index]];
  return next;
}

/**
 * Options before Salary Register download:
 * - JV grouping by master (all / department / branch / designation)
 * - Save & reorder component columns for Sheet 1
 */
export default function SalaryRegisterExportModal({
  darkMode,
  open,
  onClose,
  onConfirm,
  exporting = false,
  companyId = null,
  fixedEarningComponentNames = [],
  benefitComponentNames = [],
  fixedComponentNames = [],
  paidFixedComponentNames = [],
  variableComponentNames = [],
  earningComponentNames = [],
  deductionComponentNames = [],
  title = "Export options",
  subtitle = "Save component column order for next time",
  showJvOptions = true,
  confirmLabel = "Export Excel",
}) {
  const saved = useMemo(() => loadSalaryRegisterExportPrefs(companyId), [companyId, open]);
  const [jvGroupBy, setJvGroupBy] = useState(saved?.jvGroupBy || "all");
  const [fixedEarningOrder, setFixedEarningOrder] = useState([]);
  const [benefitOrder, setBenefitOrder] = useState([]);
  const [paidFixedOrder, setPaidFixedOrder] = useState([]);
  const [variableOrder, setVariableOrder] = useState([]);
  const [deductionOrder, setDeductionOrder] = useState([]);
  const [activeTab, setActiveTab] = useState("options");

  useEffect(() => {
    if (!open) return;
    const prefs = loadSalaryRegisterExportPrefs(companyId);
    setJvGroupBy(prefs?.jvGroupBy || "all");

    const mergeOrder = (savedList, live) => {
      const seen = new Set();
      const out = [];
      (savedList || []).forEach((n) => {
        if (live.includes(n) && !seen.has(n)) {
          seen.add(n);
          out.push(n);
        }
      });
      (live || []).forEach((n) => {
        if (!seen.has(n)) {
          seen.add(n);
          out.push(n);
        }
      });
      return out;
    };

    const liveFixedEarn =
      fixedEarningComponentNames.length > 0
        ? fixedEarningComponentNames
        : (fixedComponentNames || []).filter(
            (n) => !/\(employer\)/i.test(n) && !/gratuity/i.test(n)
          );
    const liveBenefits =
      benefitComponentNames.length > 0
        ? benefitComponentNames
        : (fixedComponentNames || []).filter(
            (n) => /\(employer\)/i.test(n) || /gratuity/i.test(n)
          );
    const livePaidFixed =
      paidFixedComponentNames.length > 0
        ? paidFixedComponentNames
        : earningComponentNames || [];
    const liveVariables = variableComponentNames || [];

    setFixedEarningOrder(
      mergeOrder(prefs?.fixedEarningOrder || prefs?.fixedOrder, liveFixedEarn)
    );
    setBenefitOrder(mergeOrder(prefs?.benefitOrder, liveBenefits));
    setPaidFixedOrder(
      mergeOrder(prefs?.paidFixedOrder || prefs?.earningOrder, livePaidFixed).filter(
        (n) => !liveVariables.some((v) => String(v).toLowerCase() === String(n).toLowerCase())
      )
    );
    setVariableOrder(mergeOrder(prefs?.variableOrder, liveVariables));
    setDeductionOrder(mergeOrder(prefs?.deductionOrder, deductionComponentNames));
    setActiveTab("options");
  }, [
    open,
    companyId,
    fixedEarningComponentNames,
    benefitComponentNames,
    fixedComponentNames,
    paidFixedComponentNames,
    variableComponentNames,
    earningComponentNames,
    deductionComponentNames,
  ]);

  if (!open) return null;

  const shell = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]";
  const muted = darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]";
  const btn = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] hover:border-[hsl(var(--ds-primary))]"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] hover:border-[hsl(var(--ds-primary))]";

  const OrderList = ({ title: listTitle, items, setItems }) => (
    <div className="min-w-0 flex-1">
      <p className={`mb-1.5 text-[11px] font-semibold ${muted}`}>{listTitle}</p>
      <ul className={`max-h-48 overflow-y-auto rounded border ${darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"}`}>
        {items.length === 0 && (
          <li className={`px-2 py-2 text-[11px] ${muted}`}>No columns yet (export once to populate)</li>
        )}
        {items.map((name, idx) => (
          <li
            key={`${listTitle}-${name}`}
            className={`flex items-center justify-between gap-1 border-b px-2 py-1 text-[11px] last:border-b-0 ${
              darkMode ? "border-[#333]" : "border-[hsl(var(--ds-border))]"
            }`}
          >
            <span className="truncate">{name}</span>
            <span className="flex shrink-0 gap-0.5">
              <button
                type="button"
                className={`rounded p-0.5 ${btn}`}
                onClick={() => setItems(moveItem(items, idx, -1))}
                title="Move up"
              >
                <ArrowUp size={12} />
              </button>
              <button
                type="button"
                className={`rounded p-0.5 ${btn}`}
                onClick={() => setItems(moveItem(items, idx, 1))}
                title="Move down"
              >
                <ArrowDown size={12} />
              </button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );

  const handleSavePrefs = () => {
    saveSalaryRegisterExportPrefs(companyId, {
      jvGroupBy,
      fixedEarningOrder,
      benefitOrder,
      paidFixedOrder,
      variableOrder,
      deductionOrder,
      // legacy keys for older prefs readers
      fixedOrder: [...fixedEarningOrder, ...benefitOrder],
      earningOrder: [...paidFixedOrder, ...variableOrder],
    });
  };

  const handleExport = () => {
    handleSavePrefs();
    onConfirm?.({
      jvGroupBy,
      columnOrder: {
        fixedEarningOrder,
        benefitOrder,
        paidFixedOrder,
        variableOrder,
        deductionOrder,
        fixedOrder: [...fixedEarningOrder, ...benefitOrder],
        earningOrder: [...paidFixedOrder, ...variableOrder],
      },
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
      <div className={`flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border shadow-xl ${shell}`}>
        <div className={`flex items-center justify-between border-b px-4 py-3 ${darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"}`}>
          <div>
            <h3 className="text-sm font-semibold">{title}</h3>
            <p className={`text-[11px] ${muted}`}>{subtitle}</p>
          </div>
          <button type="button" onClick={onClose} className={`rounded p-1 ${btn}`}>
            <X size={16} />
          </button>
        </div>

        <div className={`flex gap-2 border-b px-4 py-2 text-[11px] ${darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"}`}>
          {[
            { id: "options", label: "Options" },
            { id: "columns", label: "Column order" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`rounded px-2.5 py-1 font-medium ${
                activeTab === t.id
                  ? "bg-[hsl(var(--ds-primary))] text-white"
                  : darkMode
                    ? "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]"
                    : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {activeTab === "options" && (
            <div className="space-y-3">
              {showJvOptions ? (
                <div>
                  <p className={`mb-1.5 text-[11px] font-semibold ${muted}`}>
                    Accounting JV — group by master
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { v: "all", l: "All (company total)" },
                      { v: "department", l: "Department-wise" },
                      { v: "branch", l: "Branch-wise" },
                      { v: "designation", l: "Designation-wise" },
                    ].map((o) => (
                      <label
                        key={o.v}
                        className={`inline-flex cursor-pointer items-center gap-1.5 rounded border px-2.5 py-1.5 text-[11px] ${
                          jvGroupBy === o.v
                            ? "border-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary))]"
                            : darkMode
                              ? "border-[hsl(var(--ds-border))]"
                              : "border-[hsl(var(--ds-border))]"
                        }`}
                      >
                        <input
                          type="radio"
                          name="jvGroupBy"
                          value={o.v}
                          checked={jvGroupBy === o.v}
                          onChange={() => setJvGroupBy(o.v)}
                          className="h-3.5 w-3.5"
                        />
                        {o.l}
                      </label>
                    ))}
                  </div>
                </div>
              ) : null}
              <p className={`text-[11px] ${muted}`}>
                Column order is saved on this browser for your company and can be edited anytime under
                the Column order tab before export.
              </p>
            </div>
          )}

          {activeTab === "columns" && (
            <div className="flex flex-col gap-3 lg:flex-row">
              <OrderList
                title="Fixed earnings (E)"
                items={fixedEarningOrder}
                setItems={setFixedEarningOrder}
              />
              <OrderList
                title="Employer benefits (B)"
                items={benefitOrder}
                setItems={setBenefitOrder}
              />
              <OrderList
                title="This month — fixed"
                items={paidFixedOrder}
                setItems={setPaidFixedOrder}
              />
              <OrderList
                title="This month — variables"
                items={variableOrder}
                setItems={setVariableOrder}
              />
              <OrderList title="Deductions" items={deductionOrder} setItems={setDeductionOrder} />
            </div>
          )}
        </div>

        <div className={`flex flex-wrap items-center justify-end gap-2 border-t px-4 py-3 ${darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"}`}>
          <button type="button" onClick={onClose} className={`rounded border px-3 py-1.5 text-xs ${btn}`} disabled={exporting}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSavePrefs}
            className={`rounded border px-3 py-1.5 text-xs ${btn}`}
            disabled={exporting}
          >
            Save preferences
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 rounded bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : null}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
